import type { ProductPlan, TrafficSplit } from '../types';

export interface PriceTest {
  variants: Array<{
    name: string;
    price: number;
    interval: ProductPlan['interval'];
    trialDays: number;
  }>;
  split: TrafficSplit;
}

/**
 * The starting price test for a plan: Control at today's price, Challenger at
 * the natural price point closest to +25%, on the same billing period and
 * trial. Null for a free plan, which has no price to raise.
 */
export function priceTestForPlan(plan: ProductPlan): PriceTest | null {
  if (plan.price <= 0) return null;
  const { interval, trialPeriodDays: trialDays } = plan;
  // Never at or below today's price. (Always rounding up turned $5 into $6.99,
  // a 40% jump.)
  const challenger = naturalPriceNear(Math.round(plan.price * 1.25), plan.price);
  return {
    variants: [
      { name: 'A', price: plan.price, interval, trialDays },
      { name: 'B', price: challenger, interval, trialDays },
    ],
    split: { A: 50, B: 50 },
  };
}

/** A new price above every price in the test, so it never repeats one. */
export function nextChallengerPrice(prices: number[]): number {
  const highest = Math.max(...prices, 0);
  return naturalPriceNear(Math.round(highest * 1.25), highest);
}

/**
 * Natural price points ($X.99 under $10; $X2.99/$X5/$X9 to $50; $X9 to
 * $200; $X49/$X99 above), in cents.
 */
function naturalPricePoints(maxCents: number): number[] {
  const points: number[] = [];
  for (let d = 0; d < 10; d++) points.push(d * 100 + 99);
  for (let base = 10; base < 50; base += 10) {
    points.push((base + 2) * 100 + 99, (base + 5) * 100, (base + 9) * 100);
  }
  for (let tens = 60; tens <= 200; tens += 10) points.push(tens * 100 - 100);
  for (let fifties = 250; fifties * 100 <= maxCents * 2 + 5000; fifties += 50) {
    points.push(fifties * 100 - 100);
  }
  return points;
}

/** The natural price point nearest `targetCents` that is above `floorCents`. */
function naturalPriceNear(targetCents: number, floorCents: number): number {
  const candidates = naturalPricePoints(targetCents).filter((p) => p > floorCents);
  if (candidates.length === 0) return roundToNaturalPrice(targetCents);
  return candidates.reduce((best, p) =>
    Math.abs(p - targetCents) < Math.abs(best - targetCents) ? p : best
  );
}

/**
 * Round a price (in cents) to a natural/psychological price point
 * Examples:
 *   $7.42 → $7.99
 *   $12.49 → $12.99
 *   $47.50 → $49
 *   $123.75 → $129
 */
function roundToNaturalPrice(cents: number): number {
  const dollars = cents / 100;

  if (dollars < 10) {
    // Under $10: use $X.99 pricing (e.g., $4.99, $7.99, $9.99)
    const rounded = Math.round(dollars);
    // Ensure we're going up, not down, for price increases
    const target = rounded <= dollars ? rounded + 1 : rounded;
    return Math.max(target * 100 - 1, 99); // minimum $0.99
  } else if (dollars < 50) {
    // $10-$49: round to $X9 or $X5 endings (e.g., $12.99, $15, $19, $25, $29)
    const base = Math.floor(dollars / 10) * 10;
    const remainder = dollars - base;

    if (remainder <= 3) {
      return (base + 2) * 100 + 99; // e.g., $12.99
    } else if (remainder <= 6) {
      return (base + 5) * 100; // e.g., $15
    } else {
      return (base + 9) * 100; // e.g., $19
    }
  } else if (dollars < 200) {
    // $50-$199: round to nearest $49, $59, $69, $79, $99, etc.
    const tens = Math.round(dollars / 10) * 10;
    return tens * 100 - 100; // e.g., $49, $59, $99, $149
  } else {
    // $200+: round to nearest $X9 (e.g., $199, $249, $299, $499)
    const fifties = Math.round(dollars / 50) * 50;
    return fifties * 100 - 100;
  }
}
